import { EMPTY_BILLING_CONFIG } from '../../components/form/BillingConfigSection';
import { PARTICIPANT_TYPE_ID } from '../../constants/participantTypes';
import { BusinessPartnerData } from '../../components/form/BusinessPartnerManager';
import { mapReceivableToPayload, mapDisbursementToPayload, buildInstallmentPayload } from '../mappers/dataMappers';
import { sanitizeFileName } from '../helpers/operationHelpers';
import { Collaterals, ReceivableItem, ContractAsReceivableItem } from '../../types/operations';
import { formatDateFromBRToISO } from '../../../../../utils/DateUtils';

interface ReceivablesAssignmentPayloadParams {
    form: {
        type: string;
        operation_number: string;
        originator: string;
        approved_on: string;
        loan_amount: number;
        assignment_type_name: string;
        with_recourse: boolean;
        credit_product_type_name: string;
        receiving_account_name?: string;
        expiration_date: string;
        guarantee_percentage: number;
        total_amount: number;
        emission_date: string;
        external_link: string;
        rebate_amount: number;
        sequencial_number: number;
        receivables: { items: ReceivableItem[]; total: number; quantity: number; overcollateral?: boolean | null; spreadsheet_processed?: boolean; spreadsheet_items_count?: number; operation_id?: string };
        contracts_as_receivables: { items: ContractAsReceivableItem[]; total: number; quantity: number };
        collaterals: Collaterals;
        disbursement: {
            disbursements: any[];
            totalAmount: number;
        };
        base_calculation?: {
            tir_annual: number | null;
            tir_monthly: number | null;
            spread_annual: number | null;
            vpl: number | null;
            vpl_margin: number | null;
            duration_days: number | null;
        };
        billing_config?: any;
        installments?: any[];
    };
    creditOperationId?: string;
    selectedCedente: string;
    selectedDestinatario: string;
    assignorData: any;
    recipientData: any;
    contractFiles: File[];
    artifactFiles: File[];
    existingArtifactFiles: string[];
    removedArtifactFiles: string[];
    existingContractFile: string;
    removedContractFile: boolean;
    contractGuaranteeFile?: File | null;
    contractGuaranteeFiles?: File[];
    businessPartner?: BusinessPartnerData | null;
}

export const buildReceivablesAssignmentPayload = ({
    form,
    creditOperationId,
    selectedCedente,
    selectedDestinatario,
    assignorData,
    recipientData,
    contractFiles,
    artifactFiles,
    existingArtifactFiles,
    removedArtifactFiles,
    existingContractFile,
    removedContractFile,
    contractGuaranteeFile,
    contractGuaranteeFiles,
    businessPartner,
}: ReceivablesAssignmentPayloadParams) => {
    // Receivables e contracts são ativos principais (nível raiz do operation_dictionary)
    const receivablesPayload = {
        items: (form.receivables?.items || []).map(mapReceivableToPayload),
        total: form.receivables?.total || 0,
        quantity: form.receivables?.quantity || 1,
        spreadsheet_processed: form.receivables?.spreadsheet_processed || false,
        overcollateral: form.receivables?.overcollateral ?? null,
        operation_id: (form.receivables as any)?.operation_id || null,
    };
    console.log('[PayloadBuilder] form.receivables.operation_id:', (form.receivables as any)?.operation_id);
    console.log('[PayloadBuilder] receivablesPayload.operation_id:', receivablesPayload.operation_id);

    const contractsAsReceivablesPayload = {
        items: (form.contracts_as_receivables?.items || []).map(item => ({
            receivable_contract_id: item.receivable_contract_id,
            contract_number: item.contract_number || '',
            contract_value: item.contract_value || 0,
            contract_issue_date: item.contract_issue_date ? formatDateFromBRToISO(item.contract_issue_date) : '',
            file_name: item.file_name
                ? sanitizeFileName(item.file_name)
                : (contractGuaranteeFile ? sanitizeFileName(contractGuaranteeFile.name) : ''),
            description: item.description || '',
            drawee_person_id: item.drawee_person_id || '',
            drawee_document_type: item.drawee_document_type || '',
            drawee_document: item.drawee_document || '',
            drawee_name: item.drawee_name || '',
            drawee_address_street: item.drawee_address_street || '',
            drawee_address_number: item.drawee_address_number || '',
            drawee_address_complement: item.drawee_address_complement || '',
            drawee_address_district: item.drawee_address_district || '',
            drawee_city: item.drawee_city || '',
            drawee_state: item.drawee_state || '',
            drawee_address_zip_code: item.drawee_address_zip_code || '',
            drawee_address_ibge_code: item.drawee_address_ibge_code || '',
            document_title: item.document_title || '',
            title_installment: item.title_installment || '',
            issue_date: item.issue_date ? formatDateFromBRToISO(item.issue_date) : '',
            operation_date: item.operation_date ? formatDateFromBRToISO(item.operation_date) : '',
            present_value: item.present_value || 0,
            due_date: item.due_date ? formatDateFromBRToISO(item.due_date) : '',
            receivable_amount: item.receivable_amount || 0,
        })),
        total: form.contracts_as_receivables?.total || 0,
        quantity: form.contracts_as_receivables?.quantity || 0,
    };

    // Collaterals contém apenas garantias adicionais
    const assets = form.collaterals.assets;

    const inventoryPayload = {
        items: assets.inventory?.items || [],
        total: assets.inventory?.total || 0,
        quantity: assets.inventory?.quantity || 0,
    };

    const machineryPayload = {
        items: assets.machinery?.items || [],
        total: assets.machinery?.total || 0,
        quantity: assets.machinery?.quantity || 0,
    };

    const realEstatePayload = {
        items: assets.real_estate?.items || [],
        total: assets.real_estate?.total || 0,
        quantity: assets.real_estate?.quantity || 0,
    };

    const securitiesPayload = {
        items: assets.securities?.items || [],
        total: assets.securities?.total || 0,
        quantity: assets.securities?.quantity || 0,
    };

    // total_value e total_quantity de collaterals somam APENAS garantias (sem receivables/contracts)
    const collateralsTotalValue = inventoryPayload.total + machineryPayload.total + realEstatePayload.total + securitiesPayload.total;
    const collateralsTotalQuantity = inventoryPayload.quantity + machineryPayload.quantity + realEstatePayload.quantity + securitiesPayload.quantity;

    return {
        type: form.type || "",
        assignor: selectedCedente || "00000000-0000-0000-0000-000000000000",
        recipient: recipientData?.id || "00000000-0000-0000-0000-000000000000",
        credit_operation_id: creditOperationId,
        operation_number: form.operation_number || "",
        originator: form.originator || "",
        operation_type: "RECEIVABLES_ASSIGNMENT",
        operation_schema_id: 2,
        approved_on: formatDateFromBRToISO(form.approved_on) || null,
        emission_date: formatDateFromBRToISO(form.emission_date) || null,
        approved_value: null,
        current_balance: null,
        debt_balance_id: null,
        nominal_value: form.disbursement.totalAmount || 0,
        loan_amount: form.loan_amount || 0,
        assignment_type_name: form.assignment_type_name || "",
        with_recourse: form.with_recourse,
        credit_product_type_name: form.credit_product_type_name || "",
        receiving_account_name: form.receiving_account_name || "",
        due_date: formatDateFromBRToISO(form.expiration_date) || null,
        participants: [
            {
                id: assignorData?.id || null,
                name: assignorData?.name || '',
                document: assignorData?.document || "",
                is_active: true,
                person_id: selectedCedente || "00000000-0000-0000-0000-000000000000",
                is_primary: true,
                effective_to: null,
                effective_from: assignorData?.effective_from || new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 1,
                participation_order: 1,
                guarantee_percentage: form.guarantee_percentage,
                participant_type_name: "ASSIGNOR",
                receives_disbursement: false
            },
            {
                id: recipientData?.id || null,
                name: recipientData?.name || '',
                document: recipientData?.document || "",
                is_active: recipientData?.is_active || true,
                person_id: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                is_primary: recipientData?.is_primary || false,
                effective_to: recipientData?.effective_to || null,
                effective_from: recipientData?.effective_from || new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: 5,
                participation_order: 1,
                guarantee_percentage: null,
                participant_type_name: "RECIPIENT",
                receives_disbursement: false
            },
            ...(businessPartner ? [{
                id: businessPartner.id ? Number(businessPartner.id) : null,
                name: businessPartner.name,
                document: businessPartner.document,
                is_active: true,
                person_id: businessPartner.uuid,
                is_primary: false,
                effective_to: null,
                effective_from: new Date().toISOString(),
                guarantee_value: null,
                participant_type_id: PARTICIPANT_TYPE_ID.BUSINESS_PARTNER,
                participation_order: 3,
                guarantee_percentage: null,
                participant_type_name: "BUSINESS_PARTNER",
                receives_disbursement: false
            }] : [])
        ],
        operation_dictionary: {
            // Ativos principais no nível raiz
            receivables: receivablesPayload,
            contracts_as_receivables: contractsAsReceivablesPayload,
            // Garantias adicionais dentro de collaterals
            collaterals: {
                total_value: collateralsTotalValue,
                total_quantity: collateralsTotalQuantity,
                assets: {
                    inventory: inventoryPayload,
                    machinery: machineryPayload,
                    real_estate: realEstatePayload,
                    securities: securitiesPayload,
                }
            },
            disbursement: {
                totalAmount: form.disbursement.totalAmount || null,
                disbursements: form.disbursement.disbursements.map(mapDisbursementToPayload)
            },
            total_amount: form.total_amount || null,
            emission_date: formatDateFromBRToISO(form.emission_date) || null,
            external_link: form.external_link || "",
            rebate_amount: form.rebate_amount || null,
            expiration_date: formatDateFromBRToISO(form.expiration_date) || null,
            operation_number: form.operation_number || "",
            guarantee_percentage: form.guarantee_percentage || null,
            base_calculation: form.base_calculation ? {
                tir_annual: form.base_calculation.tir_annual,
                tir_monthly: form.base_calculation.tir_monthly,
                spread_annual: form.base_calculation.spread_annual,
                vpl: form.base_calculation.vpl,
                vpl_margin: form.base_calculation.vpl_margin,
                duration_days: form.base_calculation.duration_days
            } : undefined,
            billing_config: form.billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: (form as any).assignor_billing_address || undefined,
            ...(form.credit_product_type_name !== 'CONTRACT_ASSIGNMENT' && form.installments?.length ? {
                installment: buildInstallmentPayload(form.installments)
            } : {}),
            files: {
                contract_file: contractFiles.length > 0
                    ? `${form.sequencial_number}${sanitizeFileName(contractFiles[0].name.substring(contractFiles[0].name.lastIndexOf('.')))}`
                    : (removedContractFile ? '' : existingContractFile),
                artefacts: [
                    ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                    ...artifactFiles.map(file => sanitizeFileName(file.name)),
                    // Arquivos novos dos contratos garantia: registrar no bloco files
                    // para que o backend gere as URLs de upload correspondentes.
                    ...(contractGuaranteeFiles || []).map(file => sanitizeFileName(file.name)),
                ]
            },
        }
    };
};
